import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { X, Upload } from "lucide-react";

export default function EditProfileDialog({ profile, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: profile.name || "",
    position: profile.position || "",
    team: profile.team || "",
    country: profile.country || "",
    city: profile.city || "",
    region: profile.region || "",
    height_cm: profile.height_cm || "",
    preferred_foot: profile.preferred_foot || "",
    level_of_play: profile.level_of_play || "",
    availability: profile.availability || "",
    bio: profile.bio || "",
    avatar_url: profile.avatar_url || "",
    cover_url: profile.cover_url || "",
  });
  const [regions, setRegions] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/regions").then(({ data }) => setRegions(data.Australia || {})).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadImg = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${api.defaults.baseURL}/media/${data.path}`;
      setForm((f) => ({ ...f, [key]: url }));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.patch("/users/me", {
        ...form,
        height_cm: form.height_cm === "" ? 0 : Number(form.height_cm),
      });
      onSaved?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="edit-profile-dialog"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#121212] border border-[#262626] w-full max-w-lg rounded-sm p-6 space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl uppercase tracking-tight text-white">Edit profile</h2>
          <button
            type="button"
            data-testid="edit-close"
            onClick={onClose}
            className="text-[#a3a3a3] hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {error && (
          <div className="text-sm text-red-400 border border-red-500/40 bg-red-500/10 p-3 rounded-sm">{error}</div>
        )}
        <input
          data-testid="edit-name"
          value={form.name}
          onChange={set("name")}
          placeholder="Name"
          className="w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white transition-colors"
        />
        <div className="grid grid-cols-2 gap-3">
          <input
            data-testid="edit-position"
            value={form.position}
            onChange={set("position")}
            placeholder="Position"
            className="bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white transition-colors"
          />
          <input
            data-testid="edit-team"
            value={form.team}
            onChange={set("team")}
            placeholder="Team"
            className="bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white transition-colors"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input
            data-testid="edit-country"
            value={form.country}
            onChange={set("country")}
            placeholder="Country"
            className="bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white transition-colors"
          />
          <input
            data-testid="edit-city"
            value={form.city}
            onChange={set("city")}
            placeholder="City"
            className="bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white transition-colors"
          />
        </div>
        <select
          data-testid="edit-region"
          value={form.region}
          onChange={set("region")}
          className="w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white transition-colors"
        >
          <option value="">Region (Australia) — not set</option>
          {Object.entries(regions).map(([state, areas]) => (
            <optgroup key={state} label={state}>
              {areas.map((a) => (
                <option key={a} value={`${state} - ${a}`}>{`${state} - ${a}`}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <div className="grid grid-cols-3 gap-3">
          <input
            data-testid="edit-height"
            type="number"
            min="100"
            max="230"
            value={form.height_cm}
            onChange={set("height_cm")}
            placeholder="Height (cm)"
            className="bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white transition-colors"
          />
          <select
            data-testid="edit-foot"
            value={form.preferred_foot}
            onChange={set("preferred_foot")}
            className="bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-3 py-3 rounded-sm text-white transition-colors"
          >
            <option value="">Preferred foot</option>
            <option value="left">Left</option>
            <option value="right">Right</option>
            <option value="both">Both</option>
          </select>
          <select
            data-testid="edit-level"
            value={form.level_of_play}
            onChange={set("level_of_play")}
            className="bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-3 py-3 rounded-sm text-white transition-colors"
          >
            <option value="">Level of play</option>
            <option value="amateur">Amateur</option>
            <option value="semi-pro">Semi-pro</option>
            <option value="pro">Pro</option>
          </select>
        </div>
        {profile.role === "player" && (
          <div>
            <div className="text-xs uppercase tracking-widest text-[#737373] mb-2">
              Availability light — shown on your profile
            </div>
            <div className="grid grid-cols-3 gap-2" data-testid="edit-availability">
              {[
                { key: "green", label: "Looking for a club", dot: "bg-green-500" },
                { key: "yellow", label: "Open to offers", dot: "bg-yellow-400" },
                { key: "red", label: "Not looking", dot: "bg-red-500" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={`edit-availability-${opt.key}`}
                  onClick={() =>
                    setForm((f) => ({ ...f, availability: f.availability === opt.key ? "" : opt.key }))
                  }
                  className={`flex items-center gap-2 px-3 py-2.5 border rounded-sm text-xs text-left transition-colors ${
                    form.availability === opt.key
                      ? "border-white bg-[#1a1a1a] text-white"
                      : "border-[#262626] text-[#a3a3a3] hover:border-white hover:text-white"
                  }`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${opt.dot}`} />
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[#525252] mt-1.5">Tap again to remove the light.</div>
          </div>
        )}
        <textarea
          data-testid="edit-bio"
          value={form.bio}
          onChange={set("bio")}
          rows={3}
          placeholder="Bio"
          className="w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none p-4 rounded-sm text-white resize-none transition-colors"
        />

        <div className="grid grid-cols-2 gap-3">
          <UploadField label="Avatar" value={form.avatar_url} onChange={uploadImg("avatar_url")} testid="edit-avatar-upload" />
          <UploadField label="Cover" value={form.cover_url} onChange={uploadImg("cover_url")} testid="edit-cover-upload" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-[#262626] text-white hover:bg-[#1a1a1a] rounded-sm transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="edit-save"
            disabled={saving}
            className="px-6 py-2 bg-white text-black font-semibold rounded-sm hover:bg-gray-200 disabled:opacity-60 transition-colors"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function UploadField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[#737373] mb-1">{label}</div>
      <div className="flex items-center gap-2 border border-dashed border-[#404040] hover:border-white p-3 rounded-sm cursor-pointer transition-colors">
        <Upload className="w-4 h-4 text-[#a3a3a3]" />
        <span className="text-xs text-[#a3a3a3] truncate flex-1">
          {value ? "Replace image" : "Upload image"}
        </span>
        <input data-testid={testid} type="file" accept="image/*" className="hidden" onChange={onChange} />
      </div>
      {value && (
        <img src={value} alt="" className="mt-2 h-16 w-full object-cover border border-[#262626] rounded-sm" />
      )}
    </label>
  );
}
